import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Flex, Grid, Header, Icon, Loader, Text } from '~/components/blocks';
import {
  DirectVisitorDetailDialogProfileFragment,
  LineVisitorDetailDialogProfileFragment,
  PatientDetailDialogProfileFragment,
  WebVisitorDetailDialogProfileFragment,
} from '~/graphql/generated/graphql';
import {
  patientDetailDialogMessageState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';

import { useHasMessages } from '../../EventPane/use-has-messages';
import { MessageForm } from '../MessageForm';
import { Fields } from '../MessageForm/InputPane/types';
import { PaneType, Patient } from '../MessageForm/types';
import { useDefaultValues } from '../MessageForm/use-default_values';
import { ContactMethodType } from '../types';
import { ContactMethod } from './ContactMethod';
import { SelectContactMethod } from './SelectContactMethod';
import { useFetchOthersDraftMessages } from './use-fetch-others-draft-messages';
import { useSendMessage } from './use-send-message';
import { useUpsertDraftMessage } from './use-upsert-draft_message';

type Props = {
  patient?:
    | DirectVisitorDetailDialogProfileFragment
    | WebVisitorDetailDialogProfileFragment
    | PatientDetailDialogProfileFragment
    | LineVisitorDetailDialogProfileFragment;
};

const Wrap = styled(Grid)<{ currentPane: string }>(({ theme, currentPane }) =>
  css({
    height: '100%',
    overflow: currentPane === 'input' ? 'auto' : 'hidden',
    padding: `${theme.space.xl} ${theme.space.xl}`,
    borderLeft: `1px solid ${theme.colors.border.grey}`,
  }),
);

const InfoAlert = styled(Alert)(({ theme }) =>
  css({
    marginTop: theme.space.m,
    padding: `${theme.space.m} ${theme.space.l}`,
    fontSize: theme.fontSizes.m,
  }),
);

const EditAlert = styled(Alert)(({ theme }) =>
  css({
    marginTop: theme.space.m,
    padding: `${theme.space.m} ${theme.space.l}`,
    fontSize: theme.fontSizes.m,
  }),
);

export const InputPanel = (props: Props) => {
  const theme = useTheme();
  const [currentPane, setCurrentPane] = useState<PaneType>('input');
  const { patientId } = useAtomValue(patientDetailDialogState);
  const [patientDetailDialogMessage, setPatientDetailDialogMessage] = useAtom(
    patientDetailDialogMessageState,
  );
  const { selectedMessageId, draftMessageId, messageInfoStatus, profile } =
    patientDetailDialogMessage;
  const medixsPatient = props.patient?.medixsPatient;

  const [selectedMethod, setSelectedMethod] = useState<ContactMethodType | null>(() => {
    if (medixsPatient) {
      return medixsPatient.patient
        ? 'APP'
        : medixsPatient.guestPatients.some(
              (guestPatient) => guestPatient.__typename === 'LineVisitor',
            )
          ? 'LINE'
          : 'SMS';
    }
    return props.patient?.__typename === 'Patient'
      ? 'APP'
      : props.patient?.__typename === 'LineVisitor'
        ? 'LINE'
        : 'SMS';
  });

  const isEditable = !!selectedMessageId;

  const { sending, error, send } = useSendMessage();
  const { upserting, error: upsertError, upsert } = useUpsertDraftMessage();
  const { othersDraftMessages } = useFetchOthersDraftMessages();

  const editingByOther = othersDraftMessages.length > 0;
  const practitionerNames = othersDraftMessages.map(
    (m) => `${m.practitioner.familyName}${m.practitioner.givenName}`,
  );

  const patient: Patient = useMemo(
    () => ({
      type: profile?.type,
      name: profile ? `${profile.familyName}${profile.givenName}` : '',
      id: patientId,
      medixsPatientId: medixsPatient?.id,
    }),
    [medixsPatient?.id, patientId, profile],
  );
  const isDeactivatedPatient = props.patient?.__typename === 'Patient' && !props.patient.active;
  const isDeactivatedLineVisitor =
    props.patient?.__typename === 'LineVisitor' && !props.patient.active;

  const { loading: loadingMessages, hasMessages } = useHasMessages(props.patient);
  const { loadingMessage, loadingDraftMessage, contactMethod } = useDefaultValues(
    selectedMessageId,
    patient,
    !!selectedMessageId,
  );

  const handleBackInitial = useCallback(
    () =>
      setPatientDetailDialogMessage((_state) => ({
        ..._state,
        selectedMessageId: null,
        draftMessageId: null,
      })),
    [setPatientDetailDialogMessage],
  );
  const handleSubmit = useCallback(
    async (values: Fields, isFailed: boolean) => {
      try {
        await send(values, selectedMessageId, isFailed);
      } catch {
        // 何もしない
      }
    },
    [selectedMessageId, send],
  );
  const handleAutoSave = useCallback(
    async (values: Fields) => {
      try {
        await upsert(values, draftMessageId);
      } catch {
        // 何もしない
      }
    },
    [draftMessageId, upsert],
  );

  const handleSelectMethod = useCallback(
    (method: ContactMethodType) => {
      if (!medixsPatient) return;

      setSelectedMethod(method);
      const appSelected = method === 'APP';
      const patient = medixsPatient.patient;

      // 選択されたメソッドに応じて適切なguestPatientを取得
      let guestPatient;
      if (method === 'LINE') {
        guestPatient = medixsPatient.guestPatients.find((gp) => gp.__typename === 'LineVisitor');
      } else if (method === 'SMS') {
        guestPatient = medixsPatient.guestPatients.find(
          (gp) => gp.__typename === 'DirectVisitor' || gp.__typename === 'WebVisitor',
        );
      } else {
        guestPatient = medixsPatient.guestPatients[0];
      }

      const type = appSelected ? patient?.__typename : guestPatient?.__typename;
      const id = appSelected ? patient?.id : guestPatient?.id;
      const appointment = medixsPatient.latestAppointment;
      const appointmentId = appointment?.id || null;

      // 選択した患者が予約に紐づいているかチェック
      const isAppointmentLinked = appSelected
        ? appointment?.patient?.id === id
        : appointment?.webVisitor?.id === id;

      const profile = {
        type,
        appointmentId: isAppointmentLinked ? appointmentId : null,
        patientId: id || null,
        guestPatientId: id || null,
        familyName: patientDetailDialogMessage.profile?.familyName || '',
        givenName: patientDetailDialogMessage.profile?.givenName || '',
        phoneticFamilyName: patientDetailDialogMessage.profile?.phoneticFamilyName || '',
        phoneticGivenName: patientDetailDialogMessage.profile?.phoneticGivenName || '',
      };
      setPatientDetailDialogMessage((state) => ({
        ...state,
        profile,
      }));
    },
    [
      medixsPatient,
      patientDetailDialogMessage.profile?.familyName,
      patientDetailDialogMessage.profile?.givenName,
      patientDetailDialogMessage.profile?.phoneticFamilyName,
      patientDetailDialogMessage.profile?.phoneticGivenName,
      setPatientDetailDialogMessage,
    ],
  );

  useEffect(() => {
    // 初期表示時にアプリまたはLINEが利用できる場合は、選択患者をアプリまたはLINE患者に変更する
    const profile = patientDetailDialogMessage.profile;
    if (!profile || !selectedMethod) return;

    const needReselect =
      (selectedMethod === 'APP' && profile.type !== 'Patient') ||
      (selectedMethod === 'LINE' && profile.type !== 'LineVisitor');

    if (needReselect) {
      handleSelectMethod(selectedMethod);
    }
  }, [handleSelectMethod, patientDetailDialogMessage.profile, selectedMethod]);

  return (
    <Wrap currentPane={currentPane}>
      {sending || loadingMessage || loadingDraftMessage || loadingMessages ? (
        <Loader open inside />
      ) : (
        <Flex flexDirection="column">
          <Header>
            {`メッセージ${isEditable ? '編集' : currentPane === 'confirm' ? '確認' : '作成'}`}
          </Header>
          {editingByOther && (
            <EditAlert status="success" withIcon={false}>
              <Icon icon="edit" color="green" size="l" />
              <Text marginLeft={theme.space.m} fontWeight={theme.fontWeights.bold} size="s">
                {`${practitionerNames.join('さん、 ')}さんがメッセージを作成中です`}
              </Text>
            </EditAlert>
          )}
          {currentPane === 'input' &&
            messageInfoStatus === 'new' &&
            !hasMessages &&
            !isDeactivatedLineVisitor &&
            !isDeactivatedPatient && (
              <InfoAlert status="info">
                服薬フォローアップが必要な患者に、メッセージや質問票を送信できます。
              </InfoAlert>
            )}
          {currentPane === 'input' && messageInfoStatus === 'created' && (
            <InfoAlert status="info">メッセージを送信しました。</InfoAlert>
          )}
          {currentPane === 'input' && messageInfoStatus === 'sendAt' && (
            <InfoAlert status="info">送信予定メッセージを作成しました。</InfoAlert>
          )}
          {currentPane === 'input' && isDeactivatedPatient && (
            <InfoAlert status="error">退会済みの患者です。メッセージを送信できません</InfoAlert>
          )}
          {currentPane === 'input' && isDeactivatedLineVisitor && (
            <InfoAlert status="error">
              この患者は薬局のLINE公式アカウントをブロックしたなどの理由によりメッセージを送信できません
            </InfoAlert>
          )}
          {!isDeactivatedPatient && !isDeactivatedLineVisitor && (
            <>
              {currentPane === 'input' && selectedMethod && (
                <Flex alignItems="center" marginTop={theme.space.l}>
                  <Text paddingRight={theme.space.l}>送信先</Text>
                  {medixsPatient && !isEditable ? (
                    <SelectContactMethod
                      hasApp={!!medixsPatient.patient}
                      hasSms={medixsPatient?.guestPatients.some(
                        (guestPatient) => guestPatient.__typename !== 'LineVisitor',
                      )}
                      hasLine={medixsPatient?.guestPatients.some(
                        (guestPatient) =>
                          guestPatient.__typename === 'LineVisitor' && guestPatient.active,
                      )}
                      selectedMethod={selectedMethod}
                      onSelect={handleSelectMethod}
                    />
                  ) : (
                    <ContactMethod
                      contactMethod={
                        contactMethod ||
                        (props.patient?.__typename === 'Patient'
                          ? 'APP'
                          : props.patient?.__typename === 'LineVisitor'
                            ? 'LINE'
                            : 'SMS')
                      }
                      selected={true}
                    />
                  )}
                </Flex>
              )}
              <MessageForm
                disabled={sending}
                error={error}
                upsertError={upsertError}
                upserting={upserting}
                selectMessageId={selectedMessageId}
                patient={patient}
                onBackInitial={handleBackInitial}
                onSubmit={handleSubmit}
                onAutoSave={handleAutoSave}
                currentPane={currentPane}
                setCurrentPane={setCurrentPane}
              />
            </>
          )}
        </Flex>
      )}
    </Wrap>
  );
};
